import { defineStore } from 'pinia';
import { listCustomers } from '@/api/customers';
import api from '@/api/axiosBase';
import { getCustomer } from '@/api/project';
import type { Customer } from '@/types/types';
import { useDemoStore } from './demoStore';

export interface WalkthroughStep {
  id: string;
  /** Route to navigate to (with :projectID and :userID placeholders) */
  route?: string;
  /** CSS selector for the element to highlight. If null, shows a centered modal. */
  target?: string;
  /** Emoji icon shown in the tooltip */
  icon: string;
  /** Short title (3-6 words) */
  title: string;
  /** One-liner description */
  body: string;
  /** Where to position the tooltip relative to target */
  placement: 'top' | 'bottom' | 'left' | 'right' | 'center';
  /** Optional: action label for the CTA button (default: "Next") */
  actionLabel?: string;
  /** Optional: delay in ms before showing this step (for page transitions) */
  delay?: number;
  /** Group name for progress display */
  group: string;
  /** Whether to pulse/animate the target element */
  pulse?: boolean;
  /** Optional: open the simulate menu or trigger an action before showing */
  beforeShow?: string;
  /** If true, clicks on the spotlighted element pass through and advance the tour */
  clickThrough?: boolean;
  compact?: boolean;
  /** If true, boosts the target element above the overlay so it's directly interactive */
  interactive?: boolean;
  /** If set, renders a special full-screen showcase instead of a tooltip */
  showcase?:
    | 'welcome'
    | 'simulate'
    | 'simulate-interactive'
    | 'integrations'
    | 'forms'
    | 'forms-pick';
  /** Fallback CSS selector to highlight while primary target loads (e.g. skeleton) */
  fallbackTarget?: string;
  /** If true, this step is skipped on mobile viewports (≤640px) */
  skipOnMobile?: boolean;
  /** If true, this step only appears on mobile viewports (≤640px) */
  mobileOnly?: boolean;
  /** If true, this step is skipped when running on a demo tenant */
  skipOnDemo?: boolean;
  /** If true, scroll is locked while this step is active (for interactive steps that shouldn't scroll) */
  noScroll?: boolean;
}

export interface SubWalkthroughStep {
  id: string;
  icon: string;
  title: string;
  body: string;
  /** CSS selector for the element to highlight inside the modal */
  target?: string;
  /** Where to position the tooltip relative to the target */
  placement?: 'left' | 'right' | 'top' | 'bottom';
}

interface WalkthroughState {
  isActive: boolean;
  currentStepIndex: number;
  targetUserId: string | null;
  projectId: string | null;
  completed: boolean;
  dismissed: boolean;
  /** XP points earned (increments with each step) */
  xp: number;
  /** True when running on a real (non-demo) tenant — uses sandbox customer */
  isSandboxMode: boolean;
  /** Sub-walkthrough state (e.g. edit modal tour) */
  subWalkthroughActive: boolean;
  subWalkthroughStep: number;
  subWalkthroughSteps: SubWalkthroughStep[];
  /** The sandbox customer fetched from the backend (null until generated) */
  sandboxCustomer: Customer | null;
  /** True while the sandbox is being generated */
  sandboxLoading: boolean;
  /** True once the walkthrough AI stream (session summary, insight, messages) has completed */
  aiComplete: boolean;
  /** True once a sample lead form has been generated during the walkthrough */
  sandboxFormGenerated: boolean;
  /** True once walkthrough batch forms (6) have been generated */
  walkthroughFormsGenerated: boolean;
  /** True once a user has picked their preferred forms from the walkthrough pairs (tenant-level) */
  walkthroughFormsPicked: boolean;
}

const STORAGE_KEY = 'walkthrough_state';

function loadState(): Partial<WalkthroughState> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return {};
}

function saveState(state: WalkthroughState) {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      isActive: state.isActive,
      currentStepIndex: state.currentStepIndex,
      completed: state.completed,
      dismissed: state.dismissed,
      xp: state.xp,
      targetUserId: state.targetUserId,
      projectId: state.projectId,
      isSandboxMode: state.isSandboxMode,
      aiComplete: state.aiComplete,
      sandboxFormGenerated: state.sandboxFormGenerated,
      walkthroughFormsGenerated: state.walkthroughFormsGenerated,
      walkthroughFormsPicked: state.walkthroughFormsPicked
    })
  );
}

/** Persist onboarding state to the backend (fire-and-forget) */
function syncToBackend(
  projectId: string | null,
  fields: Record<string, unknown>
) {
  if (!projectId) return;
  api
    .put(`/v2/project/${projectId}`, fields)
    .catch((e: unknown) =>
      console.warn('[walkthrough] backend sync failed:', e)
    );
}

export const useWalkthroughStore = defineStore('walkthrough', {
  state: (): WalkthroughState => {
    const saved = loadState();
    const wasActive = !!(saved as Record<string, unknown>).isActive;
    const savedProjectId = (saved as Record<string, unknown>).projectId as
      | string
      | null;
    // Restore isActive only if it was truly in progress (not completed/dismissed) and has a project
    const shouldResume =
      wasActive && !saved.completed && !saved.dismissed && !!savedProjectId;
    return {
      isActive: shouldResume,
      currentStepIndex: saved.currentStepIndex || 0,
      targetUserId: saved.targetUserId || null,
      projectId: savedProjectId || null,
      completed: saved.completed || false,
      dismissed: saved.dismissed || false,
      xp: saved.xp || 0,
      isSandboxMode: saved.isSandboxMode || false,
      subWalkthroughActive: false,
      subWalkthroughStep: 0,
      subWalkthroughSteps: [],
      sandboxCustomer: null,
      sandboxLoading: false,
      aiComplete: saved.aiComplete || false,
      sandboxFormGenerated: saved.sandboxFormGenerated || false,
      walkthroughFormsGenerated: saved.walkthroughFormsGenerated || false,
      walkthroughFormsPicked: saved.walkthroughFormsPicked || false
    };
  },

  getters: {
    totalSteps(): number {
      const isDemo = useDemoStore().isDemoMode;
      const isMobile = window.innerWidth <= 640;
      return this.steps.filter((s) => {
        if (s.skipOnMobile && isMobile) return false;
        if (s.mobileOnly && !isMobile) return false;
        if (s.skipOnDemo && isDemo) return false;
        return true;
      }).length;
    },
    /** The 1-based visible step number (accounting for skipped steps) */
    visibleStepNumber(): number {
      const isDemo = useDemoStore().isDemoMode;
      const isMobile = window.innerWidth <= 640;
      let count = 0;
      for (
        let i = 0;
        i <= this.currentStepIndex && i < this.steps.length;
        i++
      ) {
        const s = this.steps[i];
        if (s.skipOnMobile && isMobile) continue;
        if (s.mobileOnly && !isMobile) continue;
        if (s.skipOnDemo && isDemo) continue;
        count++;
      }
      return count;
    },
    currentStep(): WalkthroughStep | null {
      if (!this.isActive || this.currentStepIndex >= this.steps.length)
        return null;
      return this.steps[this.currentStepIndex];
    },
    progress(): number {
      if (this.totalSteps === 0) return 0;
      return Math.round(((this.visibleStepNumber - 1) / this.totalSteps) * 100);
    },
    currentGroup(): string {
      return this.currentStep?.group || '';
    },
    groups(): { name: string; startIndex: number; endIndex: number }[] {
      const groups: { name: string; startIndex: number; endIndex: number }[] =
        [];
      let lastGroup = '';
      for (let i = 0; i < this.steps.length; i++) {
        if (this.steps[i].group !== lastGroup) {
          if (groups.length > 0) groups[groups.length - 1].endIndex = i - 1;
          groups.push({
            name: this.steps[i].group,
            startIndex: i,
            endIndex: i
          });
          lastGroup = this.steps[i].group;
        }
      }
      if (groups.length > 0)
        groups[groups.length - 1].endIndex = this.steps.length - 1;
      return groups;
    },
    /** All steps with placeholders resolved */
    steps(): WalkthroughStep[] {
      return WALKTHROUGH_STEPS.map((step) => ({
        ...step,
        route: step.route
          ?.replace(':projectID', this.projectId || '')
          .replace(':userID', this.targetUserId || '')
      }));
    }
  },

  actions: {
    async start(
      projectId: string,
      targetUserId?: string,
      options?: { sandbox?: boolean }
    ) {
      this.projectId = projectId;
      this.targetUserId = targetUserId || null;
      this.currentStepIndex = 0;
      this.isActive = true;
      this.completed = false;
      this.dismissed = false;
      this.isSandboxMode = options?.sandbox ?? false;
      this.xp = 0;
      this.aiComplete = false;
      this.sandboxFormGenerated = false;
      this.walkthroughFormsGenerated = false;
      saveState(this.$state);

      // In sandbox mode, generate a sandbox customer via the backend
      if (this.isSandboxMode) {
        await this.generateSandboxCustomer();
      } else if (!this.targetUserId) {
        // Auto-fetch the first customer if no target user set
        await this.fetchFirstCustomer();
      }
    },

    /** Generate a sandbox customer via the backend (calls OpenAI to build realistic data) */
    async generateSandboxCustomer() {
      if (!this.projectId) return;
      this.sandboxLoading = true;
      try {
        console.log('[walkthrough] Generating sandbox customer via backend...');
        const response = await api.post(
          `/v2/project/${this.projectId}/walkthrough/generate-sandbox`
        );
        // api.success() wraps the payload in an APIEnvelope: { data: { sandboxUserId } }
        const payload = response.data?.data || response.data;
        console.log('[walkthrough] Generate response payload:', payload);
        if (payload?.sandboxUserId) {
          this.targetUserId = payload.sandboxUserId;
          saveState(this.$state);
          console.log('[walkthrough] Sandbox user created:', this.targetUserId);
          // Now fetch the full customer data
          await this.fetchSandboxCustomer();
        }
      } catch (e) {
        console.warn(
          '[walkthrough] Sandbox generation failed, falling back to first customer:',
          e
        );
        await this.fetchFirstCustomer();
      } finally {
        this.sandboxLoading = false;
      }
    },

    /** Fetch the sandbox customer data from the API */
    async fetchSandboxCustomer() {
      if (!this.projectId || !this.targetUserId) return;
      try {
        const customer = await getCustomer(this.projectId, this.targetUserId);
        if (customer) {
          this.sandboxCustomer = customer;
          console.log('[walkthrough] Sandbox customer fetched:', customer.name);
        } else {
          // User doesn't exist (stale ID) — clear so caller can regenerate
          console.warn(
            '[walkthrough] Sandbox customer not found, clearing stale ID'
          );
          this.targetUserId = null;
          this.sandboxCustomer = null;
          saveState(this.$state);
        }
      } catch (e) {
        console.warn('[walkthrough] Failed to fetch sandbox customer:', e);
        this.targetUserId = null;
        this.sandboxCustomer = null;
        saveState(this.$state);
      }
    },

    /** Fetch the first customer from the API to use as the walkthrough target */
    async fetchFirstCustomer() {
      if (!this.projectId) return;
      try {
        const response = await listCustomers(this.projectId, 1);
        if (response?.Users?.length) {
          this.targetUserId = response.Users[0].simpleId;
          saveState(this.$state);
        }
      } catch (e) {
        console.warn('[walkthrough] Could not auto-fetch customer:', e);
      }
    },

    /** Check if a step should be skipped based on mobile/demo conditions */
    _shouldSkip(step: WalkthroughStep | undefined): boolean {
      if (!step) return false;
      const isMobile = window.innerWidth <= 640;
      if (step.skipOnMobile && isMobile) return true;
      if (step.mobileOnly && !isMobile) return true;
      if (step.skipOnDemo && useDemoStore().isDemoMode) return true;
      return false;
    },

    next() {
      if (this.currentStepIndex < this.steps.length - 1) {
        this.xp += 10;
        this.currentStepIndex++;
        // Skip steps that should be skipped (mobile/demo)
        while (
          this.currentStepIndex < this.steps.length - 1 &&
          this._shouldSkip(this.steps[this.currentStepIndex])
        ) {
          this.currentStepIndex++;
        }
        // If we landed on a skip step at the very end, complete
        if (this._shouldSkip(this.steps[this.currentStepIndex])) {
          this.complete();
          return;
        }
        saveState(this.$state);
        // Sync step progress to backend every 3 steps
        if (this.currentStepIndex % 3 === 0) {
          syncToBackend(this.projectId, {
            onboardingStep: this.currentStepIndex
          });
        }
      } else {
        this.complete();
      }
    },

    prev() {
      if (this.currentStepIndex > 0) {
        this.currentStepIndex--;
        // Skip steps that should be skipped (mobile/demo)
        while (
          this.currentStepIndex > 0 &&
          this._shouldSkip(this.steps[this.currentStepIndex])
        ) {
          this.currentStepIndex--;
        }
        saveState(this.$state);
      }
    },

    goToStep(index: number) {
      if (index >= 0 && index < this.steps.length) {
        this.currentStepIndex = index;
        // Skip steps that should be skipped (mobile/demo)
        while (
          this.currentStepIndex < this.steps.length - 1 &&
          this._shouldSkip(this.steps[this.currentStepIndex])
        ) {
          this.currentStepIndex++;
        }
        saveState(this.$state);
      }
    },

    complete() {
      this.xp += 25; // Bonus XP for finishing
      this.completed = true;
      this.isActive = false;
      saveState(this.$state);
      syncToBackend(this.projectId, {
        onboardingCompleted: true,
        onboardingStep: this.currentStepIndex
      });
    },

    dismiss() {
      this.dismissed = true;
      this.isActive = false;
      saveState(this.$state);
      syncToBackend(this.projectId, {
        onboardingDismissed: true,
        onboardingStep: this.currentStepIndex
      });
    },

    async resume(projectId: string) {
      this.projectId = projectId;
      if (!this.completed) {
        this.dismissed = false;
        this.isActive = true;
        // Skip past any excluded steps we may have saved on
        while (
          this.currentStepIndex < this.steps.length - 1 &&
          this._shouldSkip(this.steps[this.currentStepIndex])
        ) {
          this.currentStepIndex++;
        }
        saveState(this.$state);
        // In sandbox mode, re-fetch the sandbox customer if we don't have it cached
        if (this.isSandboxMode) {
          // Clear stale target from old walkthrough code
          if (this.targetUserId === '__walkthrough_sandbox__') {
            this.targetUserId = null;
          }
          if (!this.sandboxCustomer && this.targetUserId) {
            await this.fetchSandboxCustomer();
          }
          if (!this.targetUserId || !this.sandboxCustomer) {
            await this.generateSandboxCustomer();
          }
        } else if (!this.targetUserId) {
          await this.fetchFirstCustomer();
        }
      }
    },

    /** Re-fetch sandbox customer after a page refresh (called from DemoWalkthrough onMounted) */
    async hydrate() {
      if (!this.isActive || !this.projectId) return;
      if (this.isSandboxMode) {
        if (this.targetUserId && !this.sandboxCustomer) {
          await this.fetchSandboxCustomer();
        }
        if (!this.targetUserId || !this.sandboxCustomer) {
          await this.generateSandboxCustomer();
        }
      } else if (!this.targetUserId) {
        await this.fetchFirstCustomer();
      }
    },

    async restart(projectId: string, targetUserId?: string) {
      await this.start(projectId, targetUserId);
    },

    setTargetUser(userId: string) {
      this.targetUserId = userId;
      saveState(this.$state);
    },

    setAiComplete(value: boolean) {
      this.aiComplete = value;
      saveState(this.$state);
    },

    reset() {
      this.isActive = false;
      this.currentStepIndex = 0;
      this.completed = false;
      this.dismissed = false;
      this.xp = 0;
      this.targetUserId = null;
      this.isSandboxMode = false;
      this.sandboxCustomer = null;
      this.sandboxLoading = false;
      this.subWalkthroughActive = false;
      this.subWalkthroughStep = 0;
      this.subWalkthroughSteps = [];
      this.aiComplete = false;
      this.sandboxFormGenerated = false;
      this.walkthroughFormsGenerated = false;
      localStorage.removeItem(STORAGE_KEY);
    },

    /** Start a sub-walkthrough (e.g. for the edit modal) */
    startSubWalkthrough(steps: SubWalkthroughStep[]) {
      this.subWalkthroughSteps = steps;
      this.subWalkthroughStep = 0;
      this.subWalkthroughActive = true;
    },

    /** Advance to next sub-walkthrough step */
    nextSubStep() {
      if (this.subWalkthroughStep < this.subWalkthroughSteps.length - 1) {
        this.subWalkthroughStep++;
      } else {
        this.endSubWalkthrough();
      }
    },

    /** Go back one sub-walkthrough step */
    prevSubStep() {
      if (this.subWalkthroughStep > 0) {
        this.subWalkthroughStep--;
      }
    },

    /** End the sub-walkthrough and resume main tour */
    endSubWalkthrough() {
      this.subWalkthroughActive = false;
      this.subWalkthroughStep = 0;
      this.subWalkthroughSteps = [];
    },

    /** Public wrapper so components can trigger persistence */
    persistState() {
      saveState(this.$state);
    }
  }
});

// ═══════════════════════════════════════════════════════════
// WALKTHROUGH STEPS DEFINITION
// ═══════════════════════════════════════════════════════════

const WALKTHROUGH_STEPS: WalkthroughStep[] = [
  // ── WELCOME ──
  {
    id: 'welcome',
    icon: '👋',
    title: 'Welcome to MyUser AI',
    body: 'See how AI turns customer actions into personalized messages — automatically.',
    placement: 'center',
    group: 'Welcome',
    actionLabel: "Let's go →",
    showcase: 'welcome'
  },

  // ── DASHBOARD ──
  {
    id: 'dashboard-top-customer',
    route: '/dashboard/:projectID',
    target: '.item-customer-value',
    fallbackTarget: '.skeleton-customer-value',
    icon: '⭐',
    title: 'Top 5% Profile',
    body: 'An averaged profile of your highest-spending customers. Hover the trophy to see who they are.',
    placement: 'right',
    group: 'Dashboard',
    interactive: true,
    skipOnMobile: true
  },
  {
    id: 'dashboard-top-customer-stats',
    route: '/dashboard/:projectID',
    target: '.item-customer-value .stats-grid',
    fallbackTarget: '.skeleton-customer-value',
    icon: '⭐',
    title: 'Top 5% Stats',
    body: 'Average spend and purchase count of your highest-value customers — the top 5% by revenue.',
    placement: 'bottom',
    group: 'Dashboard',
    interactive: true,
    mobileOnly: true
  },
  {
    id: 'dashboard-top-customer-insights',
    route: '/dashboard/:projectID',
    target: '.item-customer-value .description',
    fallbackTarget: '.skeleton-customer-value',
    icon: '💡',
    title: 'AI Insights',
    body: 'AI-generated insights about what your best customers have in common. Use this to shape your marketing.',
    placement: 'top',
    group: 'Dashboard',
    interactive: true,
    mobileOnly: true
  },
  {
    id: 'dashboard-message-frequency',
    route: '/dashboard/:projectID',
    target: '.item-messages-chart',
    fallbackTarget: '.skeleton-messages-chart',
    icon: '📊',
    title: 'Message Frequency',
    body: 'Controls how many messages AI sends per customer per week.',
    placement: 'left',
    group: 'Dashboard',
    interactive: true
  },

  // ── CUSTOMERS ──
  {
    id: 'customers-table',
    route: '/customers/:projectID',
    target: '.sandbox-row',
    fallbackTarget: '.customers-table-wrapper',
    icon: '👥',
    title: 'Your Customers',
    body: 'Click this customer to see their full timeline.',
    placement: 'top',
    group: 'Customers',
    delay: 500,
    actionLabel: 'Open a customer →',
    clickThrough: true,
    compact: true
  },

  // ── USER TIMELINE ──
  {
    id: 'user-timeline-intro',
    route: '/user/:userID/:projectID',
    target: '.timeline-item.email-item.email-has-content .item-content',
    fallbackTarget: '.timeline-item.email-item.scheduled-item .item-content',
    icon: '✉️',
    title: 'AI-Written Email',
    body: 'AI wrote and scheduled this email based on the customer\'s activity. Click "Edit" to refine it or change the send time.',
    placement: 'left',
    group: 'Timeline',
    delay: 600,
    beforeShow: 'scroll-to-email',
    interactive: true
  },
  {
    id: 'user-timeline-filters',
    route: '/user/:userID/:projectID',
    target: '.filter-toggles',
    icon: '🔍',
    title: 'Filter by Type',
    body: 'Toggle these to show or hide activity, SMS, and email. Try it! The badges show consent status.',
    placement: 'bottom',
    group: 'Timeline',
    interactive: true,
    skipOnMobile: true
  },

  // ── SIMULATE: SHOWCASE ──
  {
    id: 'simulate-showcase',
    route: '/user/:userID/:projectID',
    icon: '⚡',
    title: 'How It Works',
    body: '',
    placement: 'center',
    group: 'Simulate',
    delay: 400,
    actionLabel: 'Try it yourself →',
    showcase: 'simulate'
  },

  // ── SIMULATE: INTERACTIVE ──
  {
    id: 'simulate-try',
    route: '/user/:userID/:projectID',
    icon: '⚡',
    title: 'Try It Now',
    body: '',
    placement: 'center',
    group: 'Simulate',
    delay: 300,
    showcase: 'simulate-interactive',
    actionLabel: 'See what happened →'
  },

  // ── POST-SIMULATE: SESSION SUMMARY ──
  {
    id: 'session-summary',
    route: '/user/:userID/:projectID',
    target: '.session-summary-item',
    icon: '🧠',
    title: 'Session Summary',
    body: 'AI grouped everything the customer just did into one session and wrote a summary. Click to expand and see every action.',
    placement: 'left',
    group: 'Results',
    delay: 400,
    beforeShow: 'refresh-timeline',
    interactive: true,
    noScroll: true,
    pulse: true
  },

  // ── POST-SIMULATE: SCHEDULED MESSAGES ──
  {
    id: 'scheduled-messages',
    route: '/user/:userID/:projectID',
    target: '.scheduled-wrapper',
    icon: '📨',
    title: 'AI Scheduled Messages',
    body: "These are scheduled email ideas. Based on the customer's actions, AI rewrote each idea to be more relevant — referencing what they browsed, searched, or added to cart.",
    placement: 'left',
    group: 'Results',
    delay: 200,
    beforeShow: 'walkthrough-messages'
  },

  // ── POST-SIMULATE: INSIGHT ──
  {
    id: 'insight-updated',
    route: '/user/:userID/:projectID',
    target: '.insight-section',
    icon: '💡',
    title: 'Updated Insight',
    body: 'AI rewrote its understanding of this customer based on everything that just happened. This drives all future decisions.',
    placement: 'right',
    group: 'Results',
    delay: 200,
    beforeShow: 'walkthrough-insight'
  },

  // ── TEMPLATES ──
  {
    id: 'templates-grid',
    route: '/templates/:projectID',
    target: '.templates-grid',
    fallbackTarget: '.skeleton-grid',
    icon: '✉️',
    title: 'Email Templates',
    body: 'AI picks one of these when sending an email, then fills in the text, images, and links for each customer. No templates = no emails.',
    placement: 'top',
    group: 'Templates',
    delay: 500
  },

  // ── FORMS ──
  {
    id: 'forms-showcase',
    route: '/forms/:projectID',
    icon: '📋',
    title: 'Lead Capture Forms',
    body: '',
    placement: 'center',
    group: 'Forms',
    delay: 500,
    actionLabel: 'Next →',
    showcase: 'forms'
  },
  {
    id: 'forms-pick',
    route: '/forms/:projectID',
    icon: '📋',
    title: 'Pick Your Forms',
    body: '',
    placement: 'center',
    group: 'Forms',
    delay: 300,
    actionLabel: 'Open picker →',
    showcase: 'forms-pick',
    beforeShow: 'generate-walkthrough-forms'
  },
  {
    id: 'forms-grid',
    route: '/forms/:projectID',
    target: '.forms-grid',
    icon: '📋',
    title: 'Your Forms',
    body: 'Each form appears on your storefront — popups, slide-ins, or banners. Every submission is automatically linked to browsing activity so AI knows exactly who they are.',
    placement: 'top',
    group: 'Forms',
    delay: 300,
    interactive: true
  },

  // ── ORGANIZATION ──
  {
    id: 'org-teammates',
    route: '/organization/:projectID',
    target: '.teammates-list',
    icon: '👥',
    title: 'Team Members',
    body: "Everyone listed here can view customers, edit templates, and monitor AI messages. The more eyes on your outreach, the better it gets. We'd really recommend inviting at least one teammate!",
    placement: 'top',
    group: 'Organization',
    interactive: true,
    delay: 800
  },
  {
    id: 'org-invite',
    route: '/organization/:projectID',
    target: '.invite-section',
    icon: '🔗',
    title: 'Invite Link',
    body: 'Share this link with a teammate and they\'ll be automatically added when they sign up — no approval step needed. You can also tap "+ Invite" above to invite by email.',
    placement: 'bottom',
    group: 'Organization',
    interactive: true,
    pulse: true,
    skipOnDemo: true,
    delay: 600
  },

  // ── INTEGRATIONS ──
  {
    id: 'integrations-intro',
    route: '/integrations/:projectID',
    icon: '🔌',
    title: 'Your Messaging Hub',
    body: '',
    placement: 'center',
    group: 'Integrations',
    delay: 500,
    actionLabel: 'Got it →',
    showcase: 'integrations'
  },
  {
    id: 'integrations-sendgrid',
    route: '/integrations/:projectID',
    target: '#sendgrid',
    icon: '📨',
    title: 'SendGrid (Optional)',
    body: "By default, we send emails on your behalf. If you'd like emails to come from your own domain — for better branding and deliverability — you can connect SendGrid here. Totally optional.",
    placement: 'bottom',
    group: 'Integrations',
    interactive: true
  },
  {
    id: 'integrations-twilio',
    route: '/integrations/:projectID',
    target: '#twilio',
    icon: '💬',
    title: 'Twilio (Optional)',
    body: 'Same idea — we send texts from a shared number by default. If you want customers to see your own phone number, connect Twilio here. Again, only if you want to.',
    placement: 'bottom',
    group: 'Integrations',
    interactive: true
  }
];
